import { t } from '@/i18n';

import { formatKrwAmount, formatKrwScaled, formatLocalizedDate, formatLocalizedNumber, getIntlLocale } from './format';

import type { DealDate, DealType, PropertyType } from '@/entities/real-estate/types';
import { defineServerEnum, reportUnknownServerValue, UNKNOWN, unknownLabel } from './server-enum';

import type { Unknown } from './server-enum';

/**
 * 부동산 금액 표기 (TRAD-2326).
 *
 * ## 무엇이 문제였나
 * 이 파일의 포맷터들은 **로케일 분기가 아예 없었다.** `억`·`만`·`만원` 을 코드에 직접 박고
 * `toLocaleString()` 을 인자 없이 호출해서, **EN 화면 5곳(지도·목록·거래·단지상세·관심목록)에
 * 한글 단위가 그대로 노출**됐다. 폴리마켓은 TRAD-2311 에서 이미 같은 문제를 풀었는데
 * 부동산만 남아 있었다.
 *
 * ## 어떻게 고쳤나 — 신규 유틸을 만들지 않는다
 * `shared/lib/utils/format.ts` 가 **이미 같은 문제를 푼 함수를 갖고 있다.** 그대로 재사용한다:
 * - `formatKrwScaled` — KO `억원/만원` · EN `M/B KRW` 로 **수 단위 체계 자체를 로케일로 가른다**.
 *   `억`·`만` 은 한국 고유 단위라 영어 대응어가 없어서, 표기 경로 이관이 아니라 단위 선택을 가른다.
 * - `formatKrwAmount` — 단위 문자열(`원`/`KRW`)을 카탈로그로 뺀 정확 표기.
 * - `formatLocalizedNumber` / `getIntlLocale` — 천단위 구분자를 보는 사람 로케일로.
 *
 * 🔴 **입력 단위는 `만원` 이다.** 부동산 API 가 만원 단위로 내려준다(`dealAmount: 285000` =
 * 28억 5천만원). 반면 `format.ts` 의 함수들은 **원** 을 받는다 — 그래서 `MAN_WON` 을 곱해
 * 넘긴다. 이 변환을 빠뜨리면 금액이 **1만 배 작게** 표시된다.
 *
 * ⚠️ KO 표기가 미세하게 바뀐다: `28.5억` → `28.5억원`, `3,000만` → `3,000만원`.
 * 기존 카탈로그 키(`format.krw_scale_*`)가 단위를 포함하기 때문이고, 같은 개념의 키를 두 벌로
 * 늘리지 않기 위해 그대로 쓴다. 가리키는 금액은 동일하다.
 */

/** 부동산 API 금액 단위(만원) → 원. */
const MAN_WON = 10_000;

/** 만원 단위 금액을 간략 표시 (KO `28.5억원`·`3,000만원` / EN `2.9B KRW`·`30M KRW`) */
export const formatRealEstatePrice = (amount: number | null | undefined): string => {
  if (amount == null) return '-';
  return formatKrwScaled(amount * MAN_WON);
};

/**
 * 단지 시세 차트(Y축·가격 태그) 전용 — TRAD-3663.
 *
 * `formatRealEstatePrice` 와 같되 **EN 10억 이상만** `B` 값을 유효숫자 3자리로 쓴다(1.775B → «1.78B», 12.34B → «12.3B»).
 * 공용 축약(`trimScale`, 소수 1자리)은 0.1B = 1억원 해상도라, 눈금 간격(실측 2,500만원)보다 거칠어
 * «1.8 / 1.8 / 1.8B KRW» 처럼 라벨이 겹쳤다. KO 축(`formatEokValue`)은 10~99억에서 0.1억 = 1,000만원이므로
 * 3자리면 EN 도 같은 해상도가 된다(P5 «유효숫자 3자리»의 EN 대칭). KO · 10억 미만 EN 은 공용 그대로.
 * 공용 `formatKrwScaled` 는 무변경 — 집계 카드 표기까지 바뀌지 않게 차트만 따로 둔다.
 *
 * 반올림은 원 정수에서 한다 — `1.775.toFixed(2)` 는 이진 표현 때문에 «1.77» 이 된다.
 */
export const formatRealEstateChartPrice = (amount: number | null | undefined): string => {
  if (amount == null) return '-';
  const won = amount * MAN_WON;
  if (getIntlLocale() === 'ko-KR' || won < 1_000_000_000) return formatKrwScaled(won);

  const billions = won / 1_000_000_000;
  const decimals = billions >= 100 ? 0 : billions >= 10 ? 1 : 2;
  const value = Math.round(won / 10 ** (9 - decimals)) / 10 ** decimals;
  // 정수(100B 이상)는 천단위 구분자 — P5 ④와 같은 규칙(KO formatEokValue 와 대칭).
  return t('format.krw_scale_billion', { value: decimals === 0 ? formatLocalizedNumber(value) : String(value) });
};

/**
 * 만원 단위 금액을 **정확 표시** (KO `28억 5,000만원` / EN `2,850,000,000 KRW`).
 *
 * 간략 표시(`formatRealEstatePrice`)와 달리 반올림하지 않는다 — 그게 이 함수가 따로 있는 이유다
 * (`28억 7,500만원` 을 `28.8억원` 으로 뭉개면 상세 화면의 의미가 없다).
 *
 * EN 은 `억`·`만` 에 대응하는 단위가 없으므로 **축약 없이 정확한 원 금액**으로 쓴다.
 * 이쪽이 `2.9B KRW` 보다 정보를 잃지 않는다 — 정확 표기가 이 함수의 존재 이유이기 때문이다.
 *
 * TRAD-2723 — `!== 'ko-KR'`(3번째 로케일 포함)가 정확한 원화 숫자로 떨어지는 것은 의도다.
 * `format.ts` `getIntlLocale()` 상단 주석(③) 참고 — 되돌리지 말 것.
 */
export const formatDealPrice = (amount: number | null | undefined): string => {
  // TRAD-2581 — 음수는 계약상 오지 않지만(backend Long) 방어가 싸다: null 과 같은 «-»(android formatRealEstateFull 과 정렬).
  if (amount == null || amount < 0) return '-';

  if (getIntlLocale() !== 'ko-KR') {
    return formatKrwAmount(amount * MAN_WON);
  }

  const eok = Math.floor(amount / 10000);
  const man = amount % 10000;

  // ⚠️ `real_estate.deal_price.eok_man` 은 **KO 전용 경로**다(EN 은 위에서 정확 표기로 빠진다).
  //    en 카탈로그 항목은 "한쪽만 = 미완성" 규칙 때문에 채워 둔 파리티 값이며, 값 자체는
  //    산술적으로 맞지만(28억 5,000만 = 2800M + 50000K) 읽기 좋지 않아 코드가 도달시키지 않는다.
  if (eok > 0 && man > 0) {
    return t('real_estate.deal_price.eok_man', {
      eok: formatLocalizedNumber(eok),
      man: formatLocalizedNumber(man),
    });
  }
  if (eok > 0) return t('format.krw_scale_eok', { value: formatLocalizedNumber(eok) });
  return t('format.krw_scale_man', { value: formatLocalizedNumber(amount) });
};

/**
 * TRAD-2581 A-6 — 대출·구매력 계산기 금액(입력 단위 = **원**). 개별 금액이라 축약하지 않는다(P6).
 * KO: 1만원 이상 → 만원 미만 버림 후 `formatDealPrice` 규칙(«3억 2,000만원»·«123만원») / 1만원 미만 → «N원».
 * EN: 원 정수 전체 «320,000,000 KRW»(월 상환액 등 소수 원은 반올림). iOS formatWon · android formatCalculatorAmount 와 같은 KO 표기.
 */
export const formatCalculatorWon = (won: number): string => {
  if (getIntlLocale() !== 'ko-KR') return formatKrwAmount(Math.round(won));
  const man = Math.floor(won / MAN_WON);
  return man >= 1 ? formatDealPrice(man) : formatKrwAmount(Math.floor(won));
};

/** DealDate → "yyyy-MM-dd" 포맷 */
export const formatDealDate = (date: DealDate): string => {
  return date ?? '-';
};

/**
 * 거래 금액 표시. 월세인 경우 "보증금/월세료" 형식, 그 외는 단순 formatRealEstatePrice.
 * 예: KO 월세 → `5,000만원/70만원`, 매매/전세 → `28.5억원`
 *     EN 월세 → `50M KRW/700,000 KRW`, 매매/전세 → `2.9B KRW`
 *
 * ⚠️ `dealType` 은 **서버가 내려주는 데이터 값**(`매매`/`전세`/`월세`)이지 화면 문자열이 아니다.
 * 그래서 이 비교는 i18n 대상이 아니다 — 여기서 번역 문자열과 비교하면 오히려 분기가 깨진다.
 */
export const formatMapDealAmount = (
  dealType: string,
  dealAmount: number | null | undefined,
  monthlyRent: number | null | undefined,
): string => {
  if (dealType === '월세' && monthlyRent != null && monthlyRent > 0) {
    return `${formatRealEstatePrice(dealAmount)}/${formatKrwScaled(monthlyRent * MAN_WON)}`;
  }
  return formatRealEstatePrice(dealAmount);
};

/**
 * 가격대 필터 라벨 (TRAD-2373).
 *
 * ## 왜 여기에 있나 — 같은 라벨을 두 곳이 각자 만들고 있었다
 * `map-page` 는 프리셋 칩 라벨을 **손으로 쓴 문자열 상수**(`'5천 이하'`)로 갖고, 선택 후 필터
 * 버튼 라벨은 **따로 계산**(`'5,000만 이하'`)했다. 같은 범위를 가리키는데 화면에 다른 글자가
 * 나온다 — TRAD-2310 이 잡았던 "조건을 복사하면 갈라진다"의 표기판이다.
 * 이제 **칩과 버튼이 이 함수 하나를 부른다.**
 *
 * 또 `map-page` 안에 `억`·`만`·`toLocaleString()` 을 직접 박은 `formatPriceLabel` 이 있었다 —
 * `formatRealEstatePrice` 의 복제였고, TRAD-2326 이 정본에서 고친 로케일 분기가 이 복제본에는
 * 없어서 **EN 화면에 `1억 이하` 가 그대로 나갔다.** 복제를 지우고 정본을 부른다.
 *
 * 🔴 **범위 미설정은 `null` 을 반환한다.** `'가격대'` 같은 자리표시자를 여기서 만들지 않는다 —
 * 그건 이 값을 쓰는 화면의 문구지 "범위"가 아니다(없는 문구를 지어내지 않는다는 TRAD-2339 원칙).
 *
 * 입력 단위는 파일 전체 규약대로 **만원**이다.
 */
export const formatPriceRange = (min: number, max: number): string | null => {
  if (!min && !max) return null;
  if (!max) return t('real_estate.price_range.at_least', { value: formatRealEstatePrice(min) });
  if (!min) return t('real_estate.price_range.at_most', { value: formatRealEstatePrice(max) });
  return t('real_estate.price_range.between', {
    min: formatRealEstatePrice(min),
    max: formatRealEstatePrice(max),
  });
};

/**
 * 전용면적 표기 — **3사 공용: 전용 ㎡ 소수 2자리 고정, 평 없음, KO/EN 동일** («59.92㎡»).
 *
 * 🔴 TRAD-2373 → 2026-09-25 결정 5(davidyoon)로 대체 (TRAD-3508). 원문은 아래 취소선으로 남긴다.
 *
 * ~~`평` 은 `억`·`만` 과 같은 부류다 — 한국 고유 단위라 영어 대응어가 없다. 그래서
 * `formatKrwScaled` 와 같은 방침을 쓴다: 표기 경로 이관이 아니라 단위 체계 자체를 로케일로
 * 가른다. KO 는 평(관용), EN 은 ㎡(서버 원값이자 국제 단위). 가리키는 면적은 같다.~~
 * ~~TRAD-2723 — `!== 'ko-KR'`(3번째 로케일 포함)가 ㎡ 로 떨어지는 것은 의도다.~~
 *
 * **왜 평을 뺐나**: 이 값은 **전용**면적이다. 전용을 평으로 바꾼 «26평»은 업계 관용 «84타입 =
 * 34평형»(공급 기준)에 익숙한 사용자에게 «작은 집»으로 오독된다. 우리 API 에는 공급면적이 없어
 * 평형을 만들 수 없다 — **재검토 조건: 공급면적 데이터가 생길 때.**
 *
 * **왜 2자리 고정인가**: 서버 값(BigDecimal)이 JSON 숫자로 오며 끝 0 이 떨어진다(84.90 → 84.9).
 * 같은 단지 59.92·59.96 이 항상 다른 글자가 되게 자릿수를 맞춘다. `toFixed` 는 로케일을 안 타서
 * 소수점이 항상 «.» 다(앱도 로케일 무관 «.» — Android `formatExclusiveArea`, iOS `RealEstateAreaFormat`).
 *
 * 소비처: 단지 상세 면적 칩·거래/전월세 목록 · 실거래 목록(trades-page) · 지도 말풍선·상세 패널
 * (map-page) · 관심목록 기준 면적·최근 거래(watchlist-page).
 *
 * 🔴 **null 이면 `'-'`** (web 의 값 없음 관례 — buildings·affordability 와 같다). backend
 * `exclu_use_ar` 는 nullable 이다(컬럼 제약 없음 · 수집 `parseDecimalSafe` 실패 시 null ·
 * 관심목록 `exclusiveArea` 는 `!= null ? … : null`). 종전 `{area}` 보간은 «null㎡» 로 끝났지만
 * `toFixed` 는 TypeError 로 **화면을 통째로 죽인다** — 그래서 가드는 호출부가 아니라 여기 한 곳에 둔다.
 */
export const formatArea = (squareMeters: number | null | undefined): string =>
  squareMeters == null || !Number.isFinite(squareMeters)
    ? '-'
    : t('real_estate.area.square_meters', { value: squareMeters.toFixed(2) });

/** 층수 표기. 숫자는 로케일 구분자를 타고, 단위 표기는 카탈로그가 정한다. */
export const formatFloor = (floor: number): string => t('real_estate.floor', { value: formatLocalizedNumber(floor) });

/**
 * 거래유형 코드(`DealType`) → 화면 라벨.
 *
 * ⚠️ 코드(`TRADE`)와 **서버가 목록 데이터에 실어 주는 한국어 값**(`'매매'`)은 다른 축이다.
 * 후자는 `formatServerDealType` 이 맡는다 — 두 축이 **같은 카탈로그 키**를 가리키게 해서
 * 문구가 갈라지지 않게 한다.
 */
export const formatDealType = (dealType: DealType): string =>
  dealType === 'JEONSE'
    ? t('real_estate.deal_type.jeonse')
    : dealType === 'MONTHLY'
      ? t('real_estate.deal_type.monthly')
      : t('real_estate.deal_type.trade');

/**
 * 지도 개별 매물 `dealType` — 서버가 **한국어 데이터 값**으로 내려준다(entities/real-estate/types `dealType: string`).
 * 값 목록과 코드(DealType) 목록은 같은 순서다(인덱스로 짝짓는다 — 한글 값을 두 번 쓰지 않으려는 것, hangul 래칫).
 */
const MAP_DEAL_TYPE_VALUES = ['매매', '전세', '월세'] as const;
const MAP_DEAL_KINDS = ['TRADE', 'JEONSE', 'MONTHLY'] as const satisfies readonly DealType[];
export const MAP_DEAL_TYPE = defineServerEnum(MAP_DEAL_TYPE_VALUES);

/** 서버 데이터 값 → 거래유형 코드. 모르는 값은 UNKNOWN(원값은 로그로 한 번). 라벨·배지색이 같은 판정을 쓴다. */
export const mapDealKind = (dealType: unknown): DealType | Unknown => {
  const value = MAP_DEAL_TYPE.parse(dealType);
  if (value === UNKNOWN) {
    reportUnknownServerValue('map.dealType', dealType);
    return UNKNOWN;
  }
  return MAP_DEAL_KINDS[MAP_DEAL_TYPE_VALUES.indexOf(value)];
};

/**
 * 서버가 내려주는 거래유형 **데이터 값**(`'매매'`/`'전세'`/`'월세'`) → 화면 라벨.
 *
 * 🔴 비교 대상인 `'월세'` 는 화면 문자열이 아니라 **서버 데이터 값**이라 i18n 대상이 아니다
 * (`formatMapDealAmount` 주석과 같은 이유). 여기서 번역문과 비교하면 분기가 통째로 깨진다.
 *
 * ~~🔴 모르는 값은 **서버 원값을 그대로 돌려준다.** 서버가 새 거래유형을 추가했을 때
 * 임의의 대체 문구를 지어내면 화면이 없는 사실을 말하게 된다 — 번역 못 한 원값이 낫다.~~
 * → 2026-10-11 정정(팀장 · TRAD-3690 후속, formatPropertyType 과 같은 판단): 모르는 값은 «알 수 없음 / Unknown»(정본
 *   Confluence 282329090 — 지어낸 문구가 아니라 정본의 표시다). 원값은 화면 대신 로그로(reportUnknownServerValue).
 *   원값(예: «단기임대»)은 EN 화면에서 한글이 그대로 새는 자리였다.
 */
export const formatServerDealType = (dealType: string): string => {
  const kind = mapDealKind(dealType);
  return kind === UNKNOWN ? unknownLabel() : formatDealType(kind);
};

/**
 * 매물유형 코드 → 화면 라벨.
 *
 * `map-page`(필터 칩)와 `watchlist-page`(배지)가 같은 3개 한글 라벨을 **각자 상수로 복사**해
 * 갖고 있었다. 한 곳만 고치면 두 화면의 같은 매물이 다른 이름으로 불린다 → 한 함수로 합친다.
 *
 * 🔴 `WatchlistItem.propertyType` 은 서버 계약상 `string` 이라 코드 유니온으로 좁힐 수 없다.
 * ~~모르는 코드는 **원값을 그대로** 돌려준다(`formatServerDealType` 과 같은 이유 — 지어낸 문구보다
 * 번역 못 한 원값이 낫다). 기존 `PROPERTY_TYPE_LABEL[...] ?? item.propertyType` 폴백과 같은 동작.~~
 * → TRAD-3690 후보⑤(팀장 10/11): 모르는 코드는 «알 수 없음 / Unknown». 위 판단은 «원값 vs 지어낸 문구»의 비교였는데,
 *   «알 수 없음»은 지어낸 문구가 아니라 정본(Confluence 282329090)의 표시다 — 원값(«VILLA» 등)은 EN/KO 어느 쪽 문구도 아니다.
 */
export const formatPropertyType = (propertyType: PropertyType | (string & {})): string => {
  if (propertyType === 'APT') return t('real_estate.property_type.apt');
  if (propertyType === 'OFFI') return t('real_estate.property_type.offi');
  if (propertyType === 'RH') return t('real_estate.property_type.rh');
  return unknownLabel();
};

/**
 * "2026년 7월" 같은 서버 `periodLabel`(TRAD-2429)에서 연/월만 뽑아낸다.
 *
 * 🔴 이 문자열은 **서버가 로케일 무관하게 한국어로 고정 포맷**한 값이다(Accept-Language를
 * 봐도 안 바뀜, 실측 확인). 그대로 화면에 렌더하면 EN 화면에 한글이 새어 나간다. 그래서
 * 문자열째로 쓰지 않고 **연/월 숫자만 뽑아 클라 로케일로 재포맷**한다(`formatPeriodBasisLabel`).
 *
 * 형식이 안 맞으면(백엔드가 포맷을 바꾸는 등) `null`을 돌려준다 — 깨진 문자열을 지어내는 것보다
 * 캡션을 안 띄우는 쪽이 안전하다(TRAD-2339 원칙과 동일).
 */
export const parsePeriodYearMonth = (label: string | null | undefined): { year: number; month: number } | null => {
  if (!label) return null;
  const m = /^(\d{4})년\s*(\d{1,2})월$/.exec(label.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
};

/**
 * 서버 `periodLabel`을 "{기간} 기준" 캡션으로 재포맷(KO "2026년 7월 기준" / EN "As of July 2026").
 * 파싱 실패 시 `null` — 호출부는 캡션 자체를 렌더하지 않는다.
 */
export const formatPeriodBasisLabel = (rawLabel: string | null | undefined): string | null => {
  const parsed = parsePeriodYearMonth(rawLabel);
  if (!parsed) return null;
  const date = new Date(parsed.year, parsed.month - 1, 1);
  const period = formatLocalizedDate(date, { year: 'numeric', month: 'long' });
  return t('real_estate.home.period_basis', { period });
};
